import { useCallback, useEffect, useMemo } from 'react';
import { bumpScrollSession } from '@teo/widget-core';
import type { ProactiveGreetingPhrase } from '@teo/widget-shared';
import { ConnectionBanner, type ConnectionBannerState } from './ConnectionBanner.js';
import { ChatHeader } from './ChatHeader.js';
import { ChatInput } from './ChatInput.js';
import { MessageList, type DemoMessage } from './MessageList.js';
import { ScrollJumpButton } from './ScrollJumpButton.js';
import { TypingIndicator } from './TypingIndicator.js';
import { useWidget } from '../context/WidgetContext.js';
import { useChatOptional } from '../chat/ChatProvider.js';
import { useChatScroll } from '../hooks/useChatScroll.js';
import type { HistoryStatus } from '../hooks/useLoadThreadHistory.js';
import { useWidgetModulesOptional } from '@teo/widget-core';
import { RichMediaProvider } from '../rich-media';

export type { ConnectionBannerState } from './ConnectionBanner.js';

export type ChatWindowProps = {
  onClose: () => void;
  /** Panel open (false = hidden but may stay mounted). */
  visible?: boolean;
  messages?: DemoMessage[];
  showWelcome?: boolean;
  /** Greeting phrases from `POST /greeting`. */
  welcomeMessages?: ProactiveGreetingPhrase[];
  /**
   * Mid-thread / after popup: pending offer merged into the timeline by
   * `pendingContextBaseTimeMs` until user reply commits it.
   */
  pendingContextMessages?: ProactiveGreetingPhrase[];
  /** Epoch ms when the offer was shown (phrase timestamps). */
  pendingContextBaseTimeMs?: number;
  /**
   * Current-page greeting phrases (from cache) — used to draw a separator
   * before backend-injected greets, not only pending ephemeral rows.
   */
  knownGreetingPhrases?: ProactiveGreetingPhrase[];
  /** Legacy single welcome (Storybook). */
  welcomeTextOverride?: string;
  showManager?: boolean;
  isTyping?: boolean;
  historyStatus?: HistoryStatus;
  onHistoryRetry?: () => void;
  isRecording?: boolean;
  onSend?: (text: string) => void;
  onRetry?: (clientId: string) => void;
  onToggleRecording?: () => void;
  connectionBanner?: ConnectionBannerState | null;
  popupDebug?: boolean;
};

export function ChatWindow({
  onClose,
  visible = true,
  messages = [],
  showWelcome = true,
  welcomeMessages,
  pendingContextMessages,
  pendingContextBaseTimeMs,
  knownGreetingPhrases,
  welcomeTextOverride,
  showManager = false,
  isTyping = false,
  historyStatus = 'idle',
  onHistoryRetry,
  isRecording = false,
  onSend,
  onRetry,
  onToggleRecording,
  connectionBanner = null,
  popupDebug = false,
}: ChatWindowProps) {
  const { welcomeText: settingsWelcomeText } = useWidget();
  const legacyWelcomeText = welcomeTextOverride ?? settingsWelcomeText;
  const effectiveWelcomeMessages = showWelcome ? welcomeMessages : undefined;
  const effectiveWelcomeText =
    showWelcome && !effectiveWelcomeMessages?.length
      ? legacyWelcomeText
      : undefined;
  const chat = useChatOptional();
  const sendImage = chat?.sendImage;
  const isUploadingImage = chat?.isUploadingImage ?? false;
  const canAttachImage =
    chat?.activeThreadId != null && Number(chat.activeThreadId) > 0;
  const modules = useWidgetModulesOptional();
  const richMediaEnabled = modules?.isEnabled('richMedia') ?? true;
  const historyLoading = historyStatus === 'loading';
  const historyError = historyStatus === 'error';
  const typingVariant =
    showManager || messages.some((message) => message.role === 'manager')
      ? 'manager'
      : 'bot';

  const lastMessage = messages[messages.length - 1];
  const forcePin = useMemo(
    () =>
      isUploadingImage ||
      (lastMessage?.role === 'user' &&
        (lastMessage.contentType === 'image' ||
          lastMessage.status === 'sending' ||
          (lastMessage.status === 'sent' &&
            Boolean(
              lastMessage.attachment?.url ||
                lastMessage.mediaUrl ||
                lastMessage.linkPreviews?.length,
            )))),
    [isUploadingImage, lastMessage],
  );

  useEffect(() => {
    const generation = bumpScrollSession('chat-window-mount');
    return () => {
      bumpScrollSession(`chat-window-unmount (gen ${generation})`);
    };
  }, []);

  useEffect(() => {
    if (!visible || !pendingContextMessages?.length) return;
    bumpScrollSession('pending-context-greeting');
  }, [visible, pendingContextMessages?.length]);

  const {
    containerRef,
    contentRef,
    newMessageCount,
    listVisible,
    scrollToBottom,
    onContentResize,
  } = useChatScroll({
    messageCount: messages.length + (pendingContextMessages?.length ?? 0),
    isTyping,
    forcePin,
    visible,
  });

  const handleJumpToBottom = useCallback(() => {
    scrollToBottom('smooth');
  }, [scrollToBottom]);

  const handleImageSelect = useCallback(
    (file: File) => {
      void sendImage?.(file);
    },
    [sendImage],
  );

  const windowBody = (
    <>
      <div className="teo-window__chrome">
        <ChatHeader onClose={onClose} showManager={showManager} />
        {connectionBanner && <ConnectionBanner state={connectionBanner} />}
      </div>
      <div className="teo-messages-wrap">
        <div
          className={`teo-messages${listVisible ? '' : ' teo-messages--preparing'}`}
          ref={containerRef}
        >
          <div className="teo-messages__content" ref={contentRef}>
            <MessageList
              messages={messages}
              welcomeMessages={effectiveWelcomeMessages}
              pendingContextMessages={pendingContextMessages}
              pendingContextBaseTimeMs={pendingContextBaseTimeMs}
              knownGreetingPhrases={knownGreetingPhrases}
              welcomeText={effectiveWelcomeText}
              historyLoading={historyLoading}
              historyError={historyError}
              onHistoryRetry={onHistoryRetry}
              onRetry={onRetry}
              onMediaLoad={onContentResize}
              embedded
              popupDebug={popupDebug}
            />
            {isTyping && (
              <TypingIndicator variant={typingVariant} />
            )}
          </div>
        </div>
        {!forcePin && newMessageCount > 0 && (
          <ScrollJumpButton
            count={newMessageCount}
            onClick={handleJumpToBottom}
          />
        )}
      </div>
      <ChatInput
        onSend={onSend}
        onImageSelect={
          sendImage && canAttachImage ? handleImageSelect : undefined
        }
        isRecording={isRecording}
        isUploadingImage={isUploadingImage}
        canAttachImage={canAttachImage}
        onToggleRecording={onToggleRecording}
      />
    </>
  );

  return (
    <div
      className={`teo-window${visible ? '' : ' teo-window--closed'}`}
      role="dialog"
      aria-label="Chat"
      aria-modal={visible ? true : undefined}
      hidden={!visible ? true : undefined}
    >
      {richMediaEnabled ? (
        <RichMediaProvider>{windowBody}</RichMediaProvider>
      ) : (
        windowBody
      )}
    </div>
  );
}
